import React from 'react';
import { useNotifications } from '../hooks/useNotifications';
import { BellIcon } from '@heroicons/react/24/outline';

const NotificationManager: React.FC = () => {
  const [waiterModalOpen, setWaiterModalOpen] = React.useState(false);
  const [waiterDetails, setWaiterDetails] = React.useState<{ table: string; reason: string } | null>(null);
  const {
    isSupported,
    isPermissionGranted,
    token,
    isInitialized
  } = useNotifications();

  // Listen for waiter call events dispatched by notificationService
  React.useEffect(() => {
    console.debug('[NotificationManager] useEffect waiterCall mount');
    const handler = (e: Event) => {
      console.debug('[NotificationManager] waiterCall event received', e);
      const detail = (e as CustomEvent).detail || {};
      console.debug('[NotificationManager] waiterCall detail', detail);
      if (detail && detail.table && detail.reason) {
        console.debug('[NotificationManager] waiterCall detail valid, opening modal');
        setWaiterDetails({ table: String(detail.table), reason: String(detail.reason) });
        setWaiterModalOpen(true);
      } else {
        console.warn('[NotificationManager] waiterCall detail missing fields', detail);
      }
    };
    window.addEventListener('waiterCall', handler as EventListener);
    console.debug('[NotificationManager] waiterCall listener attached on window');
    return () => window.removeEventListener('waiterCall', handler as EventListener);
  }, []);

  // Listen to service worker messages (background notification clicks)
  React.useEffect(() => {
    console.debug('[NotificationManager] useEffect serviceWorker message mount');
    const swHandler = (event: MessageEvent) => {
      console.debug('[NotificationManager] SW message received', event);
      const payload = event?.data || {};
      console.debug('[NotificationManager] SW message payload', payload);
      if (payload?.type === 'NOTIFICATION_NAVIGATION') {
        const data = payload?.data || {};
        console.debug('[NotificationManager] NOTIFICATION_NAVIGATION data', data);
        if (
          data.page === 'notification' &&
          (data.to === 'waiter' || data.to === 'staff') &&
          data.table
        ) {
          console.debug('[NotificationManager] SW waiter navigation data valid, opening modal');
          setWaiterDetails({ table: String(data.table), reason: String(data.reason) });
          setWaiterModalOpen(true);
        } else {
          console.warn('[NotificationManager] SW navigation data did not match waiter criteria', data);
        }
      }
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', swHandler);
      console.debug('[NotificationManager] serviceWorker message listener attached');
      return () => navigator.serviceWorker.removeEventListener('message', swHandler);
    }
  }, []);

  // Render notification status
  if (!isSupported) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <BellIcon className="h-5 w-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Notifications Not Supported
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>Your browser does not support push notifications.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isPermissionGranted) {
    // Don't show notification banner - mobile users will see modal when clicking bell icon in header
    return null;
  }

  // Render modal if waiter call received (always renderable)
  return (
    <>
      {(!isSupported || !isPermissionGranted || !isInitialized || !token) && (
        <></>
      )}

      {waiterModalOpen && waiterDetails && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setWaiterModalOpen(false)} />
          <div className="relative bg-white rounded-lg shadow-lg max-w-md w-full">
            <div className="p-4 sm:p-5">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Table Assistance Request</h3>
                <button
                  onClick={() => setWaiterModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              <div className="space-y-2">
                <p className="text-gray-800">
                  Guest is calling from <span className="font-semibold">Table {waiterDetails.table}</span>.
                </p>
                <p className="text-gray-700">
                  Message: <span className="inline-flex w-auto px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-50 text-orange-700 border border-orange-200 w-fit">
                    {waiterDetails.reason}
                  </span>
                </p>
              </div>
              <div className="mt-5 flex justify-end space-x-2">
                <button
                  onClick={() => setWaiterModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationManager;
